import CaptionUtils from '../../utils/caption';
import WebVTTParser from './webvtt-parser';
import { secondsToFormattedLabel, formattedLabelToSeconds } from '../../utils/date';

const CAPTION_RELOAD_INTERVAL_MS = 10000;
const CAPTION_SILENCE_NOTIFY_GAP = 5.0;
const CAPTION_MIN_GAP_TO_INSERT = 1.0;

function scrollToElementInsideContainer(el, container) {
  if (container && el) {
    container.scrollTop = el.offsetTop; // TODO: try to align in center of viewport
  }
}

export default class CaptionEditorController {
  constructor($state, $timeout, $scope, Broadcast, Caption, Recording, toaster) {
    'ngInject';
    this.$state = $state;
    this.$timeout = $timeout;
    this.Broadcast = Broadcast;
    this.Caption = Caption;
    this.toaster = toaster;

    this.CONFIDENCE_THRESHOLD = 0.4;

    this.id = $state.params.broadcastId;
    this.broadcast = Broadcast.get(
      { id: this.id },
      (b) => (this.recording = Recording.get({ id: b.recording_id })),
      (e) => toaster.pop('warning', 'Unable to load broadcast')
    );

    var promise = this.broadcast.$promise
      .then(() => {
        return this.safeReloadCaptions();
      })
      .then(() => {
        return this.onLoadComplete();
      })
      .catch((e) => {
        console.error(e);
      });

    this.caption = {};
    this.mode = 'full';

    this.filterByConfidenceMode = (cue) => {
      if (this.mode == 'full') {
        return true;
      } else {
        return cue.originalConfidence < 0.4;
      }
    };

    this.loadingPromises = [promise];

    $scope.$on('$destroy', () => {
      $timeout.cancel(this.captionReloadHandle);
    });
  }

  get progress() {
    if (!this.captionMeta) return 0;
    const { status, publish_status } = this.captionMeta;
    if (status == 'failed') return 0;
    if (status == 'requested') return 10;
    if (status == 'processing_audio') return 20;
    if (status == 'transcribing') return 30;
    if (status == 'processing_transcription') return 80;
    if (status == 'completed' && publish_status == 'unpublished') return 90;
    if (status == 'completed' && publish_status == 'publishing') return 95;
    return 100;
  }

  get canEdit() {
    if (this.saving) return false;
    if (this.captionMeta.publish_status == 'publishing') return false;
    return true;
  }

  set cuesDirty(val) {
    this.$state.current.confirmOnExit = val;
    this._cuesDirty = val;
  }

  get cuesDirty() {
    return this._cuesDirty;
  }

  get recordingIsTrimmed() {
    if (!this.recording) return false;
    const r = this.recording;
    return (
      (r.start_seconds != '' && r.start_seconds != 0) ||
      (r.stop_seconds != '' && r.stop_seconds != r.duration_seconds)
    );
  }

  onLoadComplete() {
    const { $timeout } = this;

    this.activeCaptionIndex = null;
    const updateActiveCaption = (currentTime) => {
      for (var i = 0; i < this.captions.length; i++) {
        var c = this.captions[i];
        if (c.start_time <= currentTime && c.end_time >= currentTime) {
          this.activeCaptionIndex = i;
          return;
        }
        if (c.start_time > currentTime) {
          this.activeCaptionIndex = null;
          return;
        }
      }
    };
    const updateActiveCaptionDebounced = _.throttle(updateActiveCaption, 500);

    this.playerOptions = {
      alwaysShowMediaControl: true,
      onPlayerStateChanged: (eventName, arg) => {
        $timeout(() => {
          this.isPlayerPlaying = eventName == 'play';
          if (eventName == 'seek') {
            updateActiveCaption(arg);
            if (this.__avoidNextAutoSeek) {
              this.__avoidNextAutoSeek = false;
            } else {
              scrollToElementInsideContainer(
                $(`#caption--index-${this.activeCaptionIndex}`)[0],
                $('.caption-table-scroll')[0]
              );
            }
          } else {
            updateActiveCaption(this.player.getCurrentTime());
          }
        }, 0);
      },
      onTimeUpdate: (t) => {
        $timeout(() => updateActiveCaptionDebounced(t), 0);
      },
      onLoadPlayer: (player) => {
        this.player = player;

        // Disable caption plugin since captions will always be displayed.
        _.each(this.player.core.plugins, (p) => {
          if (p.name === 'closed_captions') {
            p.disable();
          }
        });
      },
      trimSeconds: {
        start: 0,
        stop: 365 * 24 * 60 * 60,
      },
    };
  }

  onEditCaption(c) {
    this.cuesDirty = true;
    c.confidence = 1.0;
  }

  startCaptioning(upload = null) {
    const { Caption, toaster } = this;

    this.saving = true;

    var data = { source: 'on-demand' };
    if (upload != null) {
      data = {
        source: 'uploaded',
        status: 'complete',
        cues: upload.cues.map((c) => {
          return {
            start_time: c.startTime,
            end_time: c.endTime,
            text: c.text,
            confidence: 1.0,
          };
        }),
      };
    }

    Caption.create({ broadcast_id: this.id }, data)
      .$promise.then(() => {
        this.saving = false;
        toaster.pop('success', 'Successfully started captioning.');
        this.safeReloadCaptions();
      })
      .catch((err) => {
        this.saving = false;
        toaster.pop('warning', 'Unable to start captioning. Please refresh and try again.');
        console.error(err);
      });
  }

  save() {
    const { Caption, toaster } = this;

    // Process the UI representation of the cues
    var cuesToSave = angular.copy(this.captions).filter((c) => c.type == 'cue');

    var data = {
      publish_status: 'publishing',
      cues: cuesToSave,
    };
    this.saving = true;
    Caption.update({ broadcast_id: this.id, id: this.captionMeta.id }, data)
      .$promise.then(() => {
        this.saving = false;
        toaster.pop('success', 'Saved captions');
        this.safeReloadCaptions();
      })
      .catch((err) => {
        this.saving = false;
        toaster.pop('warning', 'Unable to save captions, please check your entry and try again.');
        console.error(err);
      });
  }

  play(caption) {
    this.__avoidNextAutoSeek = true;
    this.player.seek(caption.start_time);
    this.player.play();
  }

  processCaptionsAndInjectSilence(cues) {
    var newCaptions = [];
    var absoluteIndexCounter = 0;

    cues.forEach((c, i) => {
      // check for previous gap
      if (i > 0) {
        var lastCaption = cues[i - 1];
        if (c.start_time - lastCaption.end_time > CAPTION_SILENCE_NOTIFY_GAP) {
          newCaptions.push({
            absoluteIndex: absoluteIndexCounter++,
            type: 'silence',
            start_time: lastCaption.end_time + 0.1,
            end_time: c.start_time - 0.1,
            start_time_fmt: secondsToFormattedLabel(lastCaption.end_time + 0.1),
            end_time_fmt: secondsToFormattedLabel(c.start_time - 0.1),
          });
        }
      }

      c.absoluteIndex = absoluteIndexCounter++;
      c.type = 'cue';
      c.start_time = rounded(c.start_time);
      c.end_time = rounded(c.end_time);
      c.originalConfidence = c.confidence;
      c.start_time_fmt = secondsToFormattedLabel(c.start_time);
      c.end_time_fmt = secondsToFormattedLabel(c.end_time);
      newCaptions.push(c);
    });

    var lastCaptionEndTime =
      (newCaptions.length && newCaptions[newCaptions.length - 1].end_time) || 0;
    if (
      lastCaptionEndTime + CAPTION_MIN_GAP_TO_INSERT <
      this.broadcast.recording_duration_seconds
    ) {
      newCaptions.push({
        absoluteIndex: absoluteIndexCounter++,
        type: 'silence',
        isLast: true,
        start_time: lastCaptionEndTime + 0.1,
        end_time: this.broadcast.recording_duration_seconds,
        start_time_fmt: secondsToFormattedLabel(lastCaptionEndTime + 0.1),
        end_time_fmt: secondsToFormattedLabel(this.broadcast.recording_duration_seconds),
      });
    }

    this.captions = newCaptions;
  }

  safeReloadCaptions() {
    const { Caption, toaster, $timeout } = this;
    var captionQuery = Caption.query(
      { broadcast_id: this.id },
      (results) => {
        this.noCaptions = results.length == 0;
        this.captionMeta = _.find(results, (r) => r.recording_id == this.broadcast.recording_id);

        this.processCaptionsAndInjectSilence((this.captionMeta || {}).cues || []);

        this.cuesDirty = false;

        var publishStatus = (this.captionMeta || {}).publish_status;
        if (publishStatus == 'publishing') {
          this.captionReloadHandle = $timeout(
            () => this.safeReloadCaptions(),
            CAPTION_RELOAD_INTERVAL_MS
          );
        }
      },
      (err) => {
        toaster.pop('warning', 'Unable to load captions.');
      }
    );
    return captionQuery.$promise;
  }

  findAndReplace() {
    var find = prompt('Find Text:');
    if (!find) return;
    var regEx = new RegExp(find, 'ig');
    var matches = [];
    this.captions.forEach((c) => {
      if (c.type != 'cue') return;
      if (c.text.match(regEx)) matches.push(c);
    });
    if (!matches.length) {
      alert('No matches found');
      return;
    }
    var replace = prompt(`Found ${matches.length} matches. Replace With:`);
    if (!replace) return;
    matches.forEach((c) => {
      c.text = c.text.replace(regEx, replace);
    });
    this.cuesDirty = true;
  }

  canInsertBeforeCue(index) {
    var c = this.captions[index];
    if (c.start_time == 0) return false;
    var prev = this.captions[index - 1] || { end_time: 0 };
    if (prev.type == 'silence') {
      return true;
    } else {
      return c.start_time - prev.end_time > CAPTION_MIN_GAP_TO_INSERT;
    }
  }

  canInsertAfterCue(index) {
    var c = this.captions[index];
    var next = this.captions[index + 1];
    if (!next || next.type == 'silence') {
      return true;
    } else {
      return next.start_time - c.end_time > CAPTION_MIN_GAP_TO_INSERT;
    }
  }

  insertCueBefore(index) {
    var start_time = this.captions[index].start_time - CAPTION_SILENCE_NOTIFY_GAP;
    if (this.captions[index - 1] && this.captions[index - 1].type != 'silence') {
      if (this.captions[index - 1].end_time > start_time) {
        start_time = this.captions[index - 1].end_time + 0.1;
      }
    }
    this.captions.splice(index, 0, {
      text: 'Text...',
      start_time: Math.max(start_time, 0),
      end_time: this.captions[index].start_time - 0.1,
      confidence: 1.0,
      timestampEdit: true,
    });
    this.processCaptionsAndInjectSilence(this.captions.filter((c) => c.type != 'silence'));
    this.cuesDirty = true;
  }

  insertCueAfter(index) {
    var end_time = this.captions[index].end_time + CAPTION_SILENCE_NOTIFY_GAP;
    if (this.captions[index + 1] && this.captions[index + 1].type != 'silence') {
      if (end_time > this.captions[index + 1].start_time) {
        end_time = this.captions[index + 1].start_time - 0.1;
      }
    }
    this.captions.splice(index + 1, 0, {
      text: 'Text...',
      start_time: this.captions[index].end_time + 0.1,
      end_time: end_time,
      confidence: 1.0,
      timestampEdit: true,
    });
    this.processCaptionsAndInjectSilence(this.captions.filter((c) => c.type != 'silence'));
    this.cuesDirty = true;
  }

  deleteCue(index) {
    this.captions.splice(index, 1);
    this.processCaptionsAndInjectSilence(this.captions.filter((c) => c.type != 'silence'));
    this.cuesDirty = true;
  }

  onChangeTime(c) {
    c.start_time = formattedLabelToSeconds(c.start_time_fmt);
    c.end_time = formattedLabelToSeconds(c.end_time_fmt);
    this.processCaptionsAndInjectSilence(this.captions.filter((c) => c.type != 'silence'));
    this.cuesDirty = true;
  }

  uploadCaption(result) {
    const { $timeout } = this;

    if (!result) {
      return;
    }

    var r = new FileReader();
    r.onload = (e) => {
      $timeout(() => {
        this.parseFile(e.target.result);
      }, 100);
    };
    r.oneror = (e) => {
      $timeout(() => {
        this.uploadWebVTT = {
          errors: [{ message: 'Unable to read file.', line: 0, col: 0 }],
        };
      }, 0);
    };
    r.readAsText(result.file);
  }

  parseFile(fileContents) {
    const { $timeout } = this;

    this.parser = this.parser || new WebVTTParser();

    $timeout(() => {
      // Replace plain & with &amp; (but leave &amp; as-is);
      fileContents = fileContents.replace(/&amp;/g, '&').replace(/&/g, '&amp;');

      this.uploadWebVTT = this.parser.parse(fileContents);
      this.uploadWebVTTText = this.uploadWebVTT.cues
        .map((c) => {
          return `${secondsToFormattedLabel(c.startTime)} - ${c.text}`;
        })
        .join('\n');
    }, 0);
  }

  saveUploadedCaptions(upload) {
    const { Caption, toaster } = this;
    var data = {
      status: 'complete',
      publish_status: 'publishing',
      cues: upload.cues.map((c) => {
        return {
          start_time: c.startTime,
          end_time: c.endTime,
          text: c.text,
          confidence: 1.0,
        };
      }),
    };
    this.saving = true;
    Caption.update({ broadcast_id: this.id, id: this.captionMeta.id }, data)
      .$promise.then(() => {
        this.saving = false;
        this.captionFile = null;
        this.uploadWebVTT = null;
        this.uploadWebVTTText = null;
        this.showReplaceCaptions = null;
        toaster.pop('success', 'Saved captions');
        this.safeReloadCaptions();
      })
      .catch((err) => {
        this.saving = false;
        toaster.pop('warning', 'Unable to save captions, please check your entry and try again.');
        console.error(err);
      });
  }
}

function rounded(floatOrString, places = 4) {
  // round to at most 4 decimals, and ensure strings are cast to floats
  var f = parseFloat(floatOrString, 10);
  var str = f.toFixed(places).replace(/0+$/, '');
  return parseFloat(str, 10);
}



// WEBPACK FOOTER //
// ./app/pages/caption-editor/caption-editor-controller.js